import { ThemeEvents, CartUpdatedEvent } from '@theme/events';
import { processGifts, extractCartItemCollections } from '@theme/auto-gift-processor';

class CartDrawer extends HTMLElement {
  #rendering = false;

  constructor() {
    super();
    this.checkoutButton = null;
    this.openCartIcons = null;

    this.backdropClickHandler = this.#onBackdropClick.bind(this);
    this.checkoutButtonClickHandler = this.#handleCheckoutButtonClick.bind(this);
    this.cartUpdateHandler = this.#handleCartUpdate.bind(this);
    this.openHandler = this.open.bind(this);
    this.closeHandler = this.close.bind(this);
  }

  connectedCallback() {
    this.checkoutButton = this.querySelector('[data-ref="checkout-button"]');

    this.addEventListener('click', this.backdropClickHandler);

    this.checkoutButton.addEventListener('click', this.checkoutButtonClickHandler);

    document.addEventListener(ThemeEvents.cartUpdated, this.cartUpdateHandler);

    document.addEventListener(ThemeEvents.cartOpen, this.openHandler);
    document.addEventListener(ThemeEvents.cartClose, this.closeHandler);

    // Specific logic for Shopify Theme Editor
    if (Shopify.designMode) {
      document.addEventListener('shopify:section:unload', this.#onSectionUnload);
      if (window.theme?.isCartDrawerOpen) {
        this.open();
        window.theme.isCartDrawerOpen = false; // Reset the flag
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartClose, this.closeHandler);
    document.removeEventListener(ThemeEvents.cartOpen, this.openHandler);

    document.removeEventListener(ThemeEvents.cartUpdated, this.cartUpdateHandler);

    this.checkoutButton.removeEventListener('click', this.checkoutButtonClickHandler);

    this.removeEventListener('click', this.backdropClickHandler);

    // Specific logic for Shopify Theme Editor
    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:unload', this.#onSectionUnload);
    }
  }

  #onSectionUnload = (event) => {
    if (event.detail.sectionId !== this.dataset.sectionId) return;

    window.theme = window.theme || {};
    window.theme.isCartDrawerOpen = this.isOpen();
  };

  #handleCheckoutButtonClick(event) {
    event.target.classList.add('is-loading');
  }

  #handleCartUpdate(event) {
    this.renderCartContents(event.detail.data);
  }

  #onBackdropClick(event) {
    if (event.target.closest('dialog[open]')) return;

    const rect = this.querySelector('.cart-drawer__inner').getBoundingClientRect();
    const isInDialog = rect.top <= event.clientY && event.clientY <= rect.top + rect.height && rect.left <= event.clientX && event.clientX <= rect.left + rect.width;

    if (!isInDialog) {
      this.close();
    }
  }

  open() {
    // Nettoyer les classes pour éviter les conflits
    this.classList.remove('is-closing');
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
  }

  close() {
    // Ajouter la classe de fermeture pour déclencher l'animation
    this.classList.add('is-closing');

    // Attendre la fin de l'animation avant de masquer complètement
    setTimeout(() => {
      this.classList.remove('is-open', 'is-closing');
      document.body.classList.remove('overflow-hidden');
    }, 125); // Durée légèrement inférieure à --animation-speed pour éviter les décalages
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  #updateAutoGiftsMapping(autoGiftsHtml) {
    if (!autoGiftsHtml) return;

    const parsed = new DOMParser().parseFromString(autoGiftsHtml, 'text/html');
    const newScript = parsed.querySelector('[data-ref="cart-item-collections"]');
    const currentScript = document.querySelector('[data-ref="cart-item-collections"]');
    if (newScript && currentScript) currentScript.textContent = newScript.textContent;
  }

  async renderCartContents(parsedState) {
    if (this.#rendering) return;
    this.#rendering = true;

    try {
      const sectionId = this.dataset.sectionId;
      const newCartDrawer = new DOMParser().parseFromString(parsedState.sections[sectionId], 'text/html');

      const newAutoGiftsHtml = parsedState.sections?.['auto-gifts'];
      const newAutoGifts = newAutoGiftsHtml ? new DOMParser().parseFromString(newAutoGiftsHtml, 'text/html') : null;
      const newCollections = newAutoGifts ? extractCartItemCollections(newAutoGifts) : {};

      const giftResult = await processGifts(sectionId, newCollections);

      if (giftResult?.sections) {
        parsedState = { ...parsedState, sections: { ...parsedState.sections, ...giftResult.sections }, itemCount: giftResult.itemCount ?? parsedState.itemCount };
      }

      const finalCartDrawer = giftResult?.sections?.[sectionId]
        ? new DOMParser().parseFromString(giftResult.sections[sectionId], 'text/html')
        : newCartDrawer;

      const currentCartDrawer = this.querySelector('.cart-drawer__inner');
      currentCartDrawer.innerHTML = finalCartDrawer.querySelector('.cart-drawer__inner').innerHTML;

      this.#updateAutoGiftsMapping(parsedState.sections?.['auto-gifts']);

      setTimeout(() => {
        if (parsedState.itemCount == 0) {
          this.classList.add('cart-drawer--empty');
        } else {
          this.classList.remove('cart-drawer--empty');
        }
      });

      this.#syncCartIconCount(parsedState);
    } finally {
      this.#rendering = false;
    }
  }

  #syncCartIconCount(parsedState) {
    const cartIcon = document.querySelector('cart-icon');
    if (!cartIcon) return;

    const countEl = cartIcon.querySelector('[data-ref="cart-count"]');
    if (!countEl) return;

    if (typeof parsedState.itemCount === 'number') {
      countEl.textContent = parsedState.itemCount;
      return;
    }

    cartIcon.ensureCartCountIsCorrect();
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItem extends HTMLElement {
  constructor() {
    super();

    this.removeItemButton = null;
    this.quantitySelectorInput = null;
    this.itemRemoveHandler = this.#handleItemRemove.bind(this);
    this.quantityInputChangeHandler = this.#handleQuantityInputChange.bind(this);
  }

  connectedCallback() {
    this.removeItemButton = this.querySelector('[data-ref="remove-item"]');
    this.quantitySelectorInput = this.querySelector('[data-ref="quantity-selector-input"]');

    this.removeItemButton?.addEventListener('click', this.itemRemoveHandler);
    this.quantitySelectorInput?.addEventListener('change', this.quantityInputChangeHandler);
  }

  disconnectedCallback() {
    this.quantitySelectorInput?.removeEventListener('change', this.quantityInputChangeHandler);
    this.removeItemButton?.removeEventListener('click', this.itemRemoveHandler);
  }

  #handleItemRemove(event) {
    event.preventDefault();
    this.#updateLineItemQuantity(this.dataset.index, 0);
  }

  #handleQuantityInputChange(event) {
    event.preventDefault();
    this.#updateLineItemQuantity(this.dataset.index, this.quantitySelectorInput.value);
  }

  
    

    

async #updateLineItemQuantity(line, quantity) {
  const drawer = this.closest('cart-drawer');

  // Empêche deux modifications simultanées du panier.
  if (!drawer || drawer.dataset.calinaUpdating === 'true') return;

  drawer.dataset.calinaUpdating = 'true';
  drawer.style.pointerEvents = 'none';
  this.#enableLoading();

  const root = window.Shopify?.routes?.root || '/';
  const sectionId = drawer.dataset.sectionId;
  const sections = [sectionId, 'auto-gifts'].join(',');

  // Correspondances : Offre Duo -> Produit classique
  const duoToNormal = {
    '56001811284310': 55799977673046,
    '56001811317078': 55799977804118
  };

  const normalIds = [
    55799977673046,
    55799977804118
  ];

  const post = async (endpoint, data) => {
    const response = await fetch(root + endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        ...data,
        sections,
        sections_url: window.location.pathname
      })
    });

    const result = await response.json();

    if (!response.ok || result.errors) {
      throw new Error(
        result.description ||
        result.message ||
        'Erreur de mise à jour du panier'
      );
    }

    return result;
  };

  const getCart = async () => {
    const response = await fetch(root + 'cart.js', {
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error('Impossible de lire le panier');
    }

    return response.json();
  };

  try {
    // 1. Modifier la quantité demandée par la cliente.
    let result = await post('cart/change.js', {
      line: Number(line),
      quantity: Number(quantity)
    });

    let convertedQuantity = 0;
    let completed = false;

    // 2. Revérifier le panier après chaque conversion.
    for (let attempt = 0; attempt < 30; attempt++) {
      const cart = await getCart();

      // Seuls les surmatelas classiques achetés normalement
      // permettent de bénéficier de l'Offre Duo.
      const normalQuantity = cart.items
        .filter(item =>
          normalIds.includes(Number(item.variant_id)) &&
          item.properties?._calina_duo_converted !== '1'
        )
        .reduce((total, item) => total + item.quantity, 0);

      const duoItems = cart.items.filter(
        item => Object.prototype.hasOwnProperty.call(
          duoToNormal,
          String(item.variant_id)
        )
      );

      const duoQuantity = duoItems.reduce(
        (total, item) => total + item.quantity, 0
      );

      const excess = duoQuantity - normalQuantity;

      // Toutes les réductions restantes sont justifiées.
      if (excess <= 0) {
        completed = true;
        break;
      }

      const duoItem = duoItems[0];

      if (!duoItem) {
        throw new Error('Variante Duo introuvable');
      }

      const quantityToConvert = Math.min(
        duoItem.quantity,
        excess
      );

      const normalVariantId =
        duoToNormal[String(duoItem.variant_id)];

      const remainingQuantity =
        duoItem.quantity - quantityToConvert;

      // 3. Retirer la quantité Duo qui n'a plus droit aux -10 %.
      result = await post('cart/change.js', {
        id: duoItem.key,
        quantity: remainingQuantity
      });

      // 4. Ajouter les mêmes surmatelas au prix classique.
      result = await post('cart/add.js', {
        items: [{
          id: normalVariantId,
          quantity: quantityToConvert,
          properties: {
            ...(duoItem.properties || {}),
            _calina_duo_converted: '1'
          }
        }]
      });

      convertedQuantity += quantityToConvert;
    }

    if (!completed) {
      throw new Error(
        'Le contrôle des quantités Duo est incomplet'
      );
    }

    // 5. Vérifier une dernière fois le panier réel.
    const finalCart = await getCart();

    document.dispatchEvent(
      new CartUpdatedEvent(
        {},
        {
          itemCount: finalCart.item_count,
          source: 'cart-drawer-item',
          sections: result.sections
        }
      )
    );

    if (convertedQuantity > 0) {
      document.dispatchEvent(
        new CustomEvent('toast:open', {
          detail: {
            type: 'success',
            message:
              'Votre réduction Duo a été ajustée : ' +
              convertedQuantity +
              ' surmatelas sont repassés au prix normal.'
          }
        })
      );
    }

  } catch (error) {
    console.error('Maison Calina - Erreur panier :', error);

    document.dispatchEvent(
      new CustomEvent('toast:open', {
        detail: {
          type: 'error',
          message:
            'Impossible de mettre à jour le panier. ' +
            'Veuillez réessayer.'
        }
      })
    );

    window.location.reload();

  } finally {
    delete drawer.dataset.calinaUpdating;
    drawer.style.pointerEvents = '';
    this.#disableLoading();
  }
}



  #enableLoading() {
    this.classList.add('cart-drawer__item--loading');
  }

  #disableLoading() {
    this.classList.remove('cart-drawer__item--loading');
  }
}

if (!customElements.get('cart-drawer-item')) {
  customElements.define('cart-drawer-item', CartDrawerItem);
}
